import { useState } from 'react';
import { useLocale } from '../../../i18n/locale-context';
import { LocaleLink } from '../../../i18n/locale-link';
import { LotCard } from './lot-card';
import { formatCount, tendersWord } from '../../../lib/format';
import type { LandingNiche } from '../../../lib/landing-client';
import type { NicheTab } from '../../../content/landings/types';

interface Props {
  tabs: NicheTab[];
  niches: LandingNiche[];
  /** Шаблон с плейсхолдерами `{category}` и `{count}`. */
  foundTemplate: string;
  seeAllLabel: string;
  lockLabel: string;
  emptyLabel: string;
  onClick: (label: string) => void;
  activeLabel: string;
  daysLeftTemplate: string;
  budgetLabel: string;
  bidSecurityLabel: string;
  deadlineLabel: string;
  contactLabel: string;
  docsLabel: string;
}

/**
 * Переключатель ниш — ядро оффера `/ip`: «покажем настоящие тендеры вашего
 * размера». Лоты живые (5–100 млн, активные) и ведут на свои страницы.
 *
 * Вкладка рендерится ВСЕГДА, даже если её ниша вернулась пустой: исчезнувшая
 * вкладка молча сокращает выбор, который вся секция и предлагает сделать —
 * страница пререндерится, все восемь вкладок обязаны быть в разметке с
 * первой отрисовки, без клика.
 */
export function NicheSwitcher({
  tabs,
  niches,
  foundTemplate,
  seeAllLabel,
  lockLabel,
  emptyLabel,
  onClick,
  activeLabel,
  daysLeftTemplate,
  budgetLabel,
  bidSecurityLabel,
  deadlineLabel,
  contactLabel,
  docsLabel,
}: Props) {
  const { locale } = useLocale();
  const [active, setActive] = useState(tabs[0]?.key);
  const tab = tabs.find((t) => t.key === active) ?? tabs[0];
  const niche = niches.find((n) => n.key === active);

  return (
    <div>
      <div className="mt-6 flex flex-wrap gap-2.5" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === active}
            onClick={() => {
              setActive(t.key);
              onClick(`niche-${t.key}`);
            }}
            className={`rounded-full border px-4 py-2.5 text-[14.5px] font-bold shadow-m-sm transition ${
              t.key === active
                ? 'border-brand bg-brand-light text-brand'
                : 'border-edge bg-bone-surface text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(niche?.lots ?? []).map((lot) => (
          <LotCard
            key={lot.id}
            lot={lot}
            lockLabel={lockLabel}
            onClick={() => onClick(`lot-${niche?.key ?? ''}`)}
            activeLabel={activeLabel}
            daysLeftTemplate={daysLeftTemplate}
            budgetLabel={budgetLabel}
            bidSecurityLabel={bidSecurityLabel}
            deadlineLabel={deadlineLabel}
            contactLabel={contactLabel}
            docsLabel={docsLabel}
          />
        ))}
      </div>

      {niche && niche.lots.length === 0 && (
        <p className="mt-5 text-[15px] text-ink-soft">{emptyLabel}</p>
      )}

      {tab && niche && (
        <p className="mt-4 text-[15px] font-bold text-ink">
          {foundTemplate
            .replace('{category}', tab.categoryName)
            .replace('{count}', formatCount(niche.active))
            .replace('{word}', tendersWord(niche.active, locale))}{' '}
          ·{' '}
          <LocaleLink
            to={`/category/${niche.categorySlug}`}
            onClick={() => onClick(`niche-all-${niche.key}`)}
            className="font-bold text-brand"
          >
            {seeAllLabel}
          </LocaleLink>
        </p>
      )}
    </div>
  );
}
